import { mergeByPrecedence } from './chunk-MY2WTNZQ.js';
import { CatalogLifecycle } from './chunk-UDOQQOJI.js';

// src/precedence/overrides.ts
function isSuppressed(lifecycle) {
  return lifecycle === CatalogLifecycle.QUARANTINED;
}
function filterSuppressed(hits, lifecycleFn) {
  return [...hits].filter((hit) => !isSuppressed(lifecycleFn(hit)));
}
function applyOverrides(base, overrides, opts) {
  const all = [...base, ...overrides];
  const lifecycleFn = opts.lifecycleFn;
  const winners = mergeByPrecedence(all, opts.keyFn, opts.tierFn);
  if (!lifecycleFn) return winners;
  return winners.filter((hit) => !isSuppressed(lifecycleFn(hit)));
}

// src/precedence/risk-rules.generated.json
var risk_rules_generated_default = {
  policies: [
    {
      description: "A counterparty is a sanctioned entity or carries a sanctions tag.",
      enabled: true,
      id: "risk:ofac_sanctions",
      label: "OFAC sanctions exposure",
      max_score: 100
    },
    {
      description: "A counterparty is a mixing service, or the transaction itself is a mixing round.",
      enabled: true,
      id: "risk:mixer_exposure",
      label: "Mixer / tumbler exposure",
      max_score: 80
    },
    {
      description: "The sender carries the behavioural dust-spammer tag.",
      enabled: true,
      id: "risk:dust_spammer",
      label: "Dust spammer counterparty",
      max_score: 30
    },
    {
      description: "The sender carries the address-poisoning tag \u2014 an explorer's public name tag, or the platform's own poisoning check (a lookalike seen planting bait in its victims' histories).",
      enabled: true,
      id: "risk:address_poisoning",
      label: "Address poisoning",
      max_score: 75
    },
    {
      description: "The transaction moves a token that claims to be a well-known asset it is not.",
      enabled: true,
      id: "risk:counterfeit_token",
      label: "Counterfeit token",
      max_score: 75
    },
    {
      description: "The transaction moved a large USD value.",
      enabled: true,
      id: "risk:high_value",
      label: "High-value transaction",
      max_score: 60
    },
    {
      description: "Someone other than the account submitted its EIP-7702 delegation, and the transaction then took the account's assets or holds an authorization valid on every chain.",
      enabled: true,
      id: "risk:delegation_phishing",
      label: "EIP-7702 delegation phishing",
      max_score: 75
    },
    {
      description: "A vault deposit credited real assets but minted zero shares \u2014 the ERC-4626 first-depositor / donation share-inflation shape, where a victim's deposit truncates to no claim.",
      enabled: true,
      id: "risk:vault_share_inflation",
      label: "Vault share inflation",
      max_score: 75
    },
    {
      description: "A vault credited a deposit its stated amount while actually receiving less of a fee-on-transfer (deflationary) token \u2014 the gap a share-accounting vault then mis-prices.",
      enabled: true,
      id: "risk:fee_on_transfer_accounting",
      label: "Fee-on-transfer over-credit",
      max_score: 60
    },
    {
      description: "An address in the transaction carries a tag an address-forensics check mints \u2014 a drainer, a mixer-funded wallet, a spam token \u2014 scored by the severity the check catalogue gives it.",
      enabled: true,
      id: "risk:forensic_findings",
      label: "Address-forensics findings",
      max_score: 100
    }
  ],
  signals: [
    {
      description: "A counterparty belongs to an entity flagged as sanctioned (e.g. the OFAC SDN list).",
      enabled: true,
      id: "ofac:sanctioned_entity",
      label: "Sanctioned entity",
      policy_id: "risk:ofac_sanctions",
      severity: "critical",
      weight: 100
    },
    {
      description: "A counterparty carries a sanctions tag.",
      enabled: true,
      id: "ofac:sanctioned_tag",
      label: "Sanctions tag",
      policy_id: "risk:ofac_sanctions",
      severity: "critical",
      weight: 100
    },
    {
      description: "A counterparty is tagged as a mixing service (Tornado Cash, a tumbler).",
      enabled: true,
      id: "mixer:tagged_address",
      label: "Mixer-tagged address",
      policy_id: "risk:mixer_exposure",
      severity: "high",
      weight: 80
    },
    {
      description: "A counterparty belongs to an entity categorised as a mixer or tumbler.",
      enabled: true,
      id: "mixer:entity_match",
      label: "Mixer entity",
      policy_id: "risk:mixer_exposure",
      severity: "high",
      weight: 80
    },
    {
      description: "The transaction itself is a mixing protocol whose implementation is identified \u2014 a Wasabi, Whirlpool or JoinMarket round, a Tornado deposit.",
      enabled: true,
      id: "mixer:protocol",
      label: "Named mixing protocol",
      policy_id: "risk:mixer_exposure",
      severity: "high",
      weight: 80
    },
    {
      description: "The transaction has a CoinJoin's structure but no known implementation built it. Scored below a named mixer: the behaviour is proven, the service is not.",
      enabled: true,
      id: "mixer:structure",
      label: "CoinJoin structure, tool unidentified",
      policy_id: "risk:mixer_exposure",
      severity: "medium",
      weight: 50
    },
    {
      description: "The sender carries the behavioural dust-spammer tag.",
      enabled: true,
      id: "dust:spammer_sender",
      label: "Dust-spammer sender",
      policy_id: "risk:dust_spammer",
      severity: "low",
      weight: 25
    },
    {
      description: "The sender carries the address-poisoning tag \u2014 an explorer's public name tag, or the platform's own poisoning check (a lookalike seen planting bait in its victims' histories).",
      enabled: true,
      id: "poisoning:tagged_sender",
      label: "Address-poisoner sender",
      policy_id: "risk:address_poisoning",
      severity: "high",
      weight: 75
    },
    {
      description: "A token in the transaction calls itself by a registered asset's symbol or name (USDT, USDC, ETH) at an address that is not that asset's on this chain.",
      enabled: true,
      id: "counterfeit:token_claim",
      label: "Counterfeit token",
      policy_id: "risk:counterfeit_token",
      severity: "high",
      weight: 75
    },
    {
      description: "A token in the transaction carries the counterfeit-token catalog tag \u2014 written by the counterfeit-token check when the pipeline resolved it, or by research \u2014 so it is caught even where this transaction's own token facts were not resolved.",
      enabled: true,
      id: "counterfeit:catalog_tag",
      label: "Catalogued counterfeit token",
      policy_id: "risk:counterfeit_token",
      severity: "high",
      weight: 75
    },
    {
      description: "The transaction moved at least $1,000,000.",
      enabled: true,
      id: "high_value:gte_1m",
      label: "At least $1M moved",
      policy_id: "risk:high_value",
      severity: "high",
      weight: 60
    },
    {
      description: "The transaction moved at least $100,000.",
      enabled: true,
      id: "high_value:gte_100k",
      label: "At least $100k moved",
      policy_id: "risk:high_value",
      severity: "medium",
      weight: 40
    },
    {
      description: "Someone other than the account submitted its EIP-7702 delegation, and in the same transaction the account's assets went to that submitter, or were approved for it to spend \u2014 a phished authorization turned into a drained account. A relayer that takes its fee from the account in the same transaction matches too.",
      enabled: true,
      id: "delegation:sponsored_sweep",
      label: "Delegated by another account, then swept to it",
      policy_id: "risk:delegation_phishing",
      severity: "high",
      weight: 75
    },
    {
      description: "Someone other than the account submitted an EIP-7702 authorization the account signed for chain id 0. Whoever holds it can install the same code on the account on every EVM chain; wallets refuse to sign one for that reason.",
      enabled: true,
      id: "delegation:sponsored_any_chain",
      label: "Delegation valid on every chain, submitted by another account",
      policy_id: "risk:delegation_phishing",
      severity: "medium",
      weight: 50
    },
    {
      description: "A deposit into an ERC-4626-style vault credited a non-zero amount of assets but minted zero shares \u2014 the share-inflation victim, whose deposit the inflated exchange rate truncated to no claim. A legitimate deposit of real assets never mints zero shares.",
      enabled: true,
      id: "vault:zero_share_deposit",
      label: "Vault deposit minted zero shares for real assets",
      policy_id: "risk:vault_share_inflation",
      severity: "high",
      weight: 75
    },
    {
      description: "A vault's deposit stated more assets than the token actually transferred in \u2014 a fee-on-transfer (deflationary) token delivered less than the credited amount, the accounting gap that leaves the vault under-backed.",
      enabled: true,
      id: "vault:over_credited_deposit",
      label: "Vault credited more than it received",
      policy_id: "risk:fee_on_transfer_accounting",
      severity: "medium",
      weight: 50
    },
    {
      description: "An address in the transaction carries the funded-by-mixer tag, minted by the funding source check. Who first funded the address \u2014 an exchange withdrawal is a KYC lead; a mixer is a warning.",
      enabled: true,
      id: "forensic:funded-by-mixer",
      label: "Funded from a mixer",
      policy_id: "risk:forensic_findings",
      severity: "medium",
      weight: 50
    },
    {
      description: "An address in the transaction carries the funded-by-sanctioned tag, minted by the funding source check. Who first funded the address \u2014 an exchange withdrawal is a KYC lead; a mixer is a warning.",
      enabled: true,
      id: "forensic:funded-by-sanctioned",
      label: "Funded by a sanctioned address",
      policy_id: "risk:forensic_findings",
      severity: "high",
      weight: 75
    },
    {
      description: "An address in the transaction carries the pass-through tag, minted by the pass-through wallet check. Forwards nearly everything it receives within minutes to wallets that are not exchanges.",
      enabled: true,
      id: "forensic:pass-through",
      label: "Pass-through wallet",
      policy_id: "risk:forensic_findings",
      severity: "low",
      weight: 25
    },
    {
      description: "An address in the transaction carries the mixer-user tag, minted by the protocol, mixer and bridge usage check. Which protocols it used, read from its own transfers against our protocol address books.",
      enabled: true,
      id: "forensic:mixer-user",
      label: "Used a mixer",
      policy_id: "risk:forensic_findings",
      severity: "medium",
      weight: 50
    },
    {
      description: "An address in the transaction carries the spam-token tag, minted by the spam token check. A token pushed onto wallets that never asked for it: airdropped dust, a counterfeit, a provider's spam flag.",
      enabled: true,
      id: "forensic:spam-token",
      label: "Spam token",
      policy_id: "risk:forensic_findings",
      severity: "low",
      weight: 25
    },
    {
      description: "An address in the transaction carries the drainer tag, minted by the drainer check. Pulls tokens out of many unrelated wallets with approvals they granted \u2014 a phishing drainer's shape.",
      enabled: true,
      id: "forensic:drainer",
      label: "Drainer",
      policy_id: "risk:forensic_findings",
      severity: "critical",
      weight: 100
    },
    {
      description: "An address in the transaction carries the airdrop-farmer tag, minted by the airdrop farmer check. Claims airdrops from many distributors and passes them on to one collector.",
      enabled: true,
      id: "forensic:airdrop-farmer",
      label: "Airdrop farmer",
      policy_id: "risk:forensic_findings",
      severity: "low",
      weight: 25
    },
    {
      description: "An address in the transaction carries the scam-deployer tag, minted by the scam deployer check. Deployed contracts the catalog calls counterfeit tokens, spam tokens or drainers.",
      enabled: true,
      id: "forensic:scam-deployer",
      label: "Scam deployer",
      policy_id: "risk:forensic_findings",
      severity: "high",
      weight: 75
    }
  ]
};

// src/precedence/risk-rules.ts
var SIGNALS = new Map(
  risk_rules_generated_default.signals.map((s) => [s.id, s])
);
var POLICIES = new Map(
  risk_rules_generated_default.policies.map((p) => [p.id, p])
);
function riskSignalRule(signalId) {
  return SIGNALS.get(signalId);
}
function riskPolicyRule(policyId) {
  return POLICIES.get(policyId);
}
function riskSignalSettingPath(signalId) {
  return `risk_signals.${signalId}.weight`;
}
function fromId(id, fallback) {
  const bare = id.replace(/^risk:/, "").replace(/[_:.-]+/g, " ").trim();
  return bare ? bare.charAt(0).toUpperCase() + bare.slice(1) : fallback;
}
function riskPolicyLabel(policyId) {
  return riskPolicyRule(policyId)?.label ?? fromId(policyId, "Risk");
}
function riskSignalLabel(signalId) {
  return riskSignalRule(signalId)?.label ?? fromId(signalId, "Risk signal");
}

export { applyOverrides, filterSuppressed, isSuppressed, riskPolicyLabel, riskPolicyRule, riskSignalLabel, riskSignalRule, riskSignalSettingPath };
//# sourceMappingURL=chunk-6V6OWGS3.js.map
//# sourceMappingURL=chunk-6V6OWGS3.js.map